import { useState } from 'react'
import DataTable from '../../components/DataTable'
import Modal from '../../components/Modal'
import StatsCard from '../../components/StatsCard'
import { formatDate, formatCurrency } from '../../utils/formatters'
import { mockUsers } from '../../data/mockData'
import { Users, UserCheck, Mail, Calendar } from 'lucide-react'

/**
 * Users Management Page
 * Display and manage all app users and subscriptions
 */
export default function UsersPage() {
  const [sortKey, setSortKey] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState('desc')
  const [selectedUser, setSelectedUser] = useState(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [filterSubscription, setFilterSubscription] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  const handleRowClick = (user) => {
    setSelectedUser(user)
    setShowDetailModal(true)
  }

  // Filter users
  let filteredUsers = mockUsers
  if (filterSubscription !== 'All') {
    filteredUsers = filteredUsers.filter((u) => u.subscription === filterSubscription)
  }
  if (filterStatus !== 'All') {
    filteredUsers = filteredUsers.filter((u) => u.status === filterStatus)
  }

  // Calculate metrics
  const totalUsers = mockUsers.length
  const activeUsers = mockUsers.filter((u) => u.status === 'Active').length
  const totalLifetimeSpend = mockUsers.reduce((sum, u) => sum + u.lifetimeSpend, 0)
  const paidUsers = mockUsers.filter((u) => u.subscription !== 'Free').length

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
    },
    {
      key: 'pets',
      label: 'Pets',
      sortable: false,
      render: (value) => `${value} pet${value > 1 ? 's' : ''}`,
    },
    {
      key: 'subscription',
      label: 'Subscription',
      sortable: true,
      render: (value) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value === 'Plus'
              ? 'bg-pawssible-teal-100 text-pawssible-teal-700'
              : value === 'Family'
                ? 'bg-pawssible-amber-100 text-pawssible-amber-700'
                : 'bg-pawssible-slate-100 text-pawssible-slate-700'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span
          className={`px-2 py-1 rounded text-xs font-medium ${
            value === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Joined',
      sortable: true,
      render: formatDate,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Users</h1>
        <p className="text-pawssible-slate-600 mt-1">Manage all app users and subscriptions</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Total Users"
          value={totalUsers}
          icon={Users}
          color="teal"
        />
        <StatsCard
          label="Active Users"
          value={activeUsers}
          icon={UserCheck}
          color="amber"
        />
        <StatsCard
          label="Paid Users"
          value={paidUsers}
          icon={Mail}
          color="teal"
        />
        <StatsCard
          label="Lifetime Revenue"
          value={`AED ${totalLifetimeSpend.toLocaleString()}`}
          icon={Calendar}
          color="amber"
        />
      </div>

      {/* Filter Controls */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Filter by Subscription</label>
            <select
              value={filterSubscription}
              onChange={(e) => setFilterSubscription(e.target.value)}
              className="input"
            >
              <option>All</option>
              <option>Free</option>
              <option>Plus</option>
              <option>Family</option>
            </select>
          </div>
          <div>
            <label className="label">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Add User</button>
        <button className="btn-secondary">Export CSV</button>
        <button className="btn-secondary">Send Announcement</button>
      </div>

      {/* Users Table */}
      <DataTable
        columns={columns}
        data={filteredUsers}
        onSort={handleSort}
        sortKey={sortKey}
        sortOrder={sortOrder}
        pageSize={15}
        onRowClick={handleRowClick}
      />

      {/* User Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={selectedUser ? `User Profile: ${selectedUser.name}` : ''}
        size="lg"
        actions={
          <>
            <button
              onClick={() => setShowDetailModal(false)}
              className="btn-secondary"
            >
              Close
            </button>
            <button className="btn-primary">Send Email</button>
            <button className="btn-danger">Suspend Account</button>
          </>
        }
      >
        {selectedUser && (
          <div className="space-y-6">
            {/* User Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Name</p>
                <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedUser.name}</p>
              </div>
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Email</p>
                <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedUser.email}</p>
              </div>
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Phone</p>
                <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedUser.phone}</p>
              </div>
              <div>
                <p className="text-xs text-pawssible-slate-600 uppercase tracking-wider">Status</p>
                <span className={`inline-block px-2 py-1 rounded text-xs font-medium mt-1 ${
                  selectedUser.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {selectedUser.status}
                </span>
              </div>
            </div>

            {/* Subscription Info */}
            <div className="border-t border-pawssible-slate-200 pt-4">
              <h4 className="text-lg font-semibold text-pawssible-slate-900 mb-3">Subscription</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-pawssible-slate-600">Plan</p>
                  <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedUser.subscription}</p>
                </div>
                <div>
                  <p className="text-xs text-pawssible-slate-600">Number of Pets</p>
                  <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{selectedUser.pets}</p>
                </div>
                <div>
                  <p className="text-xs text-pawssible-slate-600">Lifetime Spend</p>
                  <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">AED {selectedUser.lifetimeSpend}</p>
                </div>
                <div>
                  <p className="text-xs text-pawssible-slate-600">Joined</p>
                  <p className="text-lg font-semibold text-pawssible-slate-900 mt-1">{formatDate(selectedUser.createdAt)}</p>
                </div>
              </div>
            </div>

            {/* Activity Info */}
            <div className="border-t border-pawssible-slate-200 pt-4">
              <h4 className="text-lg font-semibold text-pawssible-slate-900 mb-3">Activity</h4>
              <p className="text-pawssible-slate-600">Last Active: {formatDate(selectedUser.lastActive)}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
